import { Alert } from "react-bootstrap";

import { Card, CardGrid } from "@bagel-web/components";

import ASHComponentCard from "../agent/stateComponents/ASHComponentCard";
import { seedMessagesToKVStoreState } from "../agent/stateComponents/kvStore/kvStoreSeed";
import { seedMessagesToLimitedListState } from "../agent/stateComponents/limitedList/limitedListSeed";
import { AgentViewPreferencesProvider } from "../agent/viewPreferences/agentViewPreferences";
import PlaygroundMessageList from "./PlaygroundMessageList";
import {
  CommonComponentPresentation,
  CommonTypedComponentState,
  PlaygroundAgentConfig,
  PlaygroundComponentConfig,
  PlaygroundComponentMessages,
  PlaygroundSeedMessage,
  PlaygroundSource,
  PlaygroundUnsupportedComponent,
} from "../types";

// A component's seed presented as the state the shared Agent History cards
// render, for the types whose editors have landed; null for the rest.
const presentComponentSeed = (
  component: PlaygroundComponentConfig & { id: string },
  messages: Array<PlaygroundSeedMessage>
): {
  componentState: CommonTypedComponentState;
  componentPresentation?: CommonComponentPresentation;
} | null => {
  switch (component.type) {
    case "limited_list":
      return {
        componentState: seedMessagesToLimitedListState(component.id, messages),
      };
    case "kv_store":
      return seedMessagesToKVStoreState(
        component.id,
        messages,
        typeof component.delimiter === "string" ? component.delimiter : null
      );
    default:
      return null;
  }
};

/**
 * Step 4 — Agent State.
 *
 * Each configured component's seed messages, with channels kept invisible.
 * Limited lists and kv stores render through the shared Agent History state
 * cards, presenting the seed as component state; other component types keep
 * a plain message list until their editors land the same way. Components
 * stripped from the reconstruction render as warning cards.
 */
const PlaygroundAgentStateStep = ({
  agentConfig,
  componentMessages,
  unsupportedComponents,
  source,
  onComponentMessagesChange,
}: {
  agentConfig: PlaygroundAgentConfig;
  componentMessages: PlaygroundComponentMessages;
  unsupportedComponents: Array<PlaygroundUnsupportedComponent>;
  // Where the draft was seeded from — the shared state cards' link target;
  // null for a from-scratch draft, whose items carry no provenance to link.
  source: PlaygroundSource | null;
  // The state editors' apply path: every edit lands as a full replacement
  // of one component's messages.
  onComponentMessagesChange: (
    componentId: string,
    messages: Array<PlaygroundSeedMessage>
  ) => void;
}) => {
  const components = (agentConfig.component_configs ?? []).filter(
    (component): component is { id: string } & typeof component =>
      typeof component.id === "string"
  );

  return (
    <AgentViewPreferencesProvider>
      <CardGrid maxColumns={2} unitWidth={1750}>
        {components.map((component) => {
          const messages = componentMessages[component.id] ?? [];
          const presented = presentComponentSeed(component, messages);
          if (presented) {
            return (
              <ASHComponentCard
                key={component.id}
                isEditMode={true}
                componentState={presented.componentState}
                componentPresentation={presented.componentPresentation}
                sessionId={source?.sessionId ?? ""}
                agentId={source?.agentId ?? ""}
                messages={messages}
                onMessagesChange={(next) =>
                  onComponentMessagesChange(component.id, next)
                }
              />
            );
          }
          return (
            <Card
              key={component.id}
              title={
                component.type
                  ? `${component.id} (${component.type})`
                  : component.id
              }
            >
              <PlaygroundMessageList messages={messages} />
            </Card>
          );
        })}
        {unsupportedComponents.map((component) => (
          <Card
            key={component.id}
            title={`${component.id} (${component.type})`}
          >
            <Alert
              variant="warning"
              className="mb-0"
              data-testid={`playground-unsupported-${component.id}`}
            >
              The {component.type} component type isn't supported by the
              Playground, so this component's state is not seeded.
            </Alert>
          </Card>
        ))}
      </CardGrid>
    </AgentViewPreferencesProvider>
  );
};

export default PlaygroundAgentStateStep;
